import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { environment } from '../../config/environment';
import { apiRequest } from '../../services/apiClient';
import createSidebarSections from '../../config/sidebarSections';
import DashboardLayout from '../../components/DashboardLayout';
import '../../pages/WelcomePage.css';
import '../../pages/LoginPage.css';
import './MasterPage.css';

const MASTER_CONFIG = {
  posts: {
    key: 'posts',
    navLabel: 'Posts',
    singularLabel: 'Post',
    pluralLabel: 'Posts',
    inputLabel: 'Post Name',
    inputPlaceholder: 'Enter post name',
    searchPlaceholder: 'Search posts...',
    tableHeader: 'POST NAME',
    endpoints: {
      add: environment.endpoints.masterPostsAdd,
      getAll: environment.endpoints.masterPostsGetAll,
      getOne: environment.endpoints.masterPostsGetOne,
      update: environment.endpoints.masterPostsUpdate,
      delete: environment.endpoints.masterPostsDelete,
    },
  },
  educationType: {
    key: 'educationType',
    navLabel: 'Education Type',
    singularLabel: 'Education Type',
    pluralLabel: 'Education Types',
    inputLabel: 'Education Type Name',
    inputPlaceholder: 'Enter education type',
    searchPlaceholder: 'Search education type...',
    tableHeader: 'EDUCATION TYPE',
    endpoints: {
      add: environment.endpoints.masterEducationTypeAdd,
      getAll: environment.endpoints.masterEducationTypeGetAll,
      getOne: environment.endpoints.masterEducationTypeGetOne,
      update: environment.endpoints.masterEducationTypeUpdate,
      delete: environment.endpoints.masterEducationTypeDelete,
    },
  },
  jobType: {
    key: 'jobType',
    navLabel: 'Job Type',
    singularLabel: 'Job Type',
    pluralLabel: 'Job Types',
    inputLabel: 'Job Type Name',
    inputPlaceholder: 'Enter job type',
    searchPlaceholder: 'Search job type...',
    tableHeader: 'JOB TYPE',
    endpoints: {
      add: environment.endpoints.masterJobTypeAdd,
      getAll: environment.endpoints.masterJobTypeGetAll,
      getOne: environment.endpoints.masterJobTypeGetOne,
      update: environment.endpoints.masterJobTypeUpdate,
      delete: environment.endpoints.masterJobTypeDelete,
    },
  },
  // designation: {
  //   key: 'designation',
  //   navLabel: 'Designation',
  //   singularLabel: 'Designation',
  //   pluralLabel: 'Designations',
  //   inputLabel: 'Designation Name',
  //   inputPlaceholder: 'Enter designation',
  //   searchPlaceholder: 'Search designation...',
  //   tableHeader: 'DESIGNATION',
  //   endpoints: {
  //     add: environment.endpoints.masterDesignationAdd,
  //     getAll: environment.endpoints.masterDesignationGetAll,
  //     getOne: environment.endpoints.masterDesignationGetOne,
  //     update: environment.endpoints.masterDesignationUpdate,
  //     delete: environment.endpoints.masterDesignationDelete,
  //   },
  // },
};

const STATUS_CODE = {
  ACTIVE: '1',
  INACTIVE: '2',
  DELETED: '9',
};

const MasterPage = () => {
  const navigate = useNavigate();
  const [activeMasterKey, setActiveMasterKey] = useState('posts');
  const [posts, setPosts] = useState([]);
  const [isPostsLoading, setIsPostsLoading] = useState(false);
  const [postsError, setPostsError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [addForm, setAddForm] = useState({ name: '', status: STATUS_CODE.ACTIVE, sequence: '' });
  const [editForm, setEditForm] = useState({ name: '', status: STATUS_CODE.ACTIVE, sequence: '' });
  const [nextSequence, setNextSequence] = useState('');

  const activeMaster = MASTER_CONFIG[activeMasterKey] || MASTER_CONFIG.posts;

  const userName = useMemo(() => {
    const userStr = localStorage.getItem('authUser') || sessionStorage.getItem('authUser');
    if (!userStr) return 'Rakesh';

    try {
      const user = JSON.parse(userStr);
      return user?.name || user?.fullName || user?.email || 'Rakesh';
    } catch {
      return 'Rakesh';
    }
  }, []);

  const renderLogoLoader = () => (
    <div className="mini-logo-loader" role="status" aria-live="polite" aria-label="Loading">
      <span className="loader-orbit loader-orbit-one"></span>
      <span className="loader-orbit loader-orbit-two"></span>
      <span className="loader-orbit loader-orbit-three"></span>
      <img
        src={process.env.PUBLIC_URL + '/images/logo.png'}
        alt="Company Logo"
        className="session-loader-logo"
      />
    </div>
  );

  const calculateNextSequence = useCallback((postsList) => {
    if (!Array.isArray(postsList) || postsList.length === 0) {
      return '1';
    }

    const sequences = postsList
      .map((item) => Number(item?.sequence))
      .filter((seq) => !isNaN(seq));

    if (sequences.length === 0) {
      return '1';
    }

    const maxSequence = Math.max(...sequences);
    return String(maxSequence + 1);
  }, []);

  const fetchPosts = useCallback(async () => {
    setIsPostsLoading(true);
    setPostsError('');

    try {
      const { ok, data } = await apiRequest(activeMaster.endpoints.getAll, {
        method: 'POST',
      });

      if (!ok || data?.response !== true) {
        throw new Error(data?.message || `Unable to fetch ${activeMaster.pluralLabel.toLowerCase()}.`);
      }

      const postsList = Array.isArray(data?.data) ? data.data : [];
      setPosts(postsList);
      
      // Calculate next sequence number
      const nextSeq = calculateNextSequence(postsList);
      setNextSequence(nextSeq);
    } catch (error) {
      setPosts([]);
      setPostsError(error?.message || `Unable to fetch ${activeMaster.pluralLabel.toLowerCase()}.`);
      setNextSequence('1');
    } finally {
      setIsPostsLoading(false);
    }
  }, [activeMaster, calculateNextSequence]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key !== 'Escape' || isSubmitting) {
        return;
      }

      if (isEditModalOpen) {
        setIsEditModalOpen(false);
        return;
      }

      if (isViewModalOpen) {
        setIsViewModalOpen(false);
        return;
      }

      if (isAddModalOpen) {
        setIsAddModalOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isAddModalOpen, isViewModalOpen, isEditModalOpen, isSubmitting]);

  const normalizeStatus = (status) => {
    const normalized = String(status);

    if (normalized === STATUS_CODE.ACTIVE) {
      return 'Active';
    }

    if (normalized === STATUS_CODE.INACTIVE) {
      return 'Inactive';
    }

    if (normalized === STATUS_CODE.DELETED) {
      return 'Deleted';
    }

    return 'Inactive';
  };

  const getStatusClassName = (status) => {
    const normalized = String(status);

    if (normalized === STATUS_CODE.ACTIVE) {
      return 'is-active';
    }

    if (normalized === STATUS_CODE.INACTIVE) {
      return 'is-inactive';
    }

    if (normalized === STATUS_CODE.DELETED) {
      return 'is-deleted';
    }

    return 'is-inactive';
  };

  const visiblePosts = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) {
      return posts;
    }

    return posts.filter((post) => (post?.name || '').toLowerCase().includes(query));
  }, [posts, searchText]);

  const validatePostForm = (form) => {
    const nextErrors = {};

    if (!form.name.trim()) {
      nextErrors.name = `${activeMaster.singularLabel} name is required.`;
    }

    if (!form.sequence || form.sequence.trim() === '') {
      nextErrors.sequence = 'Sequence is required.';
    } else if (isNaN(Number(form.sequence)) || Number(form.sequence) <= 0) {
      nextErrors.sequence = 'Sequence must be a positive number.';
    }

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const getCurrentLocationOrNull = () => new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve({ latitude: null, longitude: null });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      () => {
        resolve({ latitude: null, longitude: null });
      },
      {
        enableHighAccuracy: true,
        timeout: 8000,
        maximumAge: 0,
      }
    );
  });

  const fetchOnePost = async (id) => {
    const { ok, data } = await apiRequest(`${activeMaster.endpoints.getOne}/${id}`, {
      method: 'POST',
    });

    if (!ok || data?.response !== true) {
      throw new Error(data?.message || `Unable to fetch ${activeMaster.singularLabel.toLowerCase()} details.`);
    }

    return data?.data || null;
  };

  const openViewModal = async (id) => {
    setIsSubmitting(true);

    try {
      const post = await fetchOnePost(id);
      setSelectedPost(post);
      setIsViewModalOpen(true);
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        text: error?.message || `Unable to fetch ${activeMaster.singularLabel.toLowerCase()} details.`,
        confirmButtonColor: '#c7374e',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditModal = async (id) => {
    setIsSubmitting(true);

    try {
      const post = await fetchOnePost(id);
      setSelectedPost(post);
      setEditForm({
        name: post?.name || '',
        status: post?.status !== undefined && post?.status !== null
          ? String(post.status)
          : STATUS_CODE.ACTIVE,
        sequence: post?.sequence !== undefined && post?.sequence !== null
          ? String(post.sequence)
          : '',
      });
      setFormErrors({});
      setIsEditModalOpen(true);
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        text: error?.message || `Unable to fetch ${activeMaster.singularLabel.toLowerCase()} details.`,
        confirmButtonColor: '#c7374e',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddChange = (event) => {
    const { name, value } = event.target;
    setAddForm((prev) => ({ ...prev, [name]: value }));

    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleEditChange = (event) => {
    const { name, value } = event.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));

    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleAddSubmit = async (event) => {
    event.preventDefault();
    if (!validatePostForm(addForm)) {
      return;
    }

    setIsSubmitting(true);

    try {
      const coords = await getCurrentLocationOrNull();

      const { ok, data } = await apiRequest(activeMaster.endpoints.add, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: addForm.name.trim(),
          status: Number(addForm.status),
          sequence: Number(addForm.sequence),
          latitude: coords.latitude,
          longitude: coords.longitude,
        }),
      });

      setIsSubmitting(false);

      if (!ok || data?.response !== true) {
        await Swal.fire({
          icon: 'error',
          text: data?.message || `Unable to add ${activeMaster.singularLabel.toLowerCase()}.`,
          confirmButtonColor: '#c7374e',
        });
        return;
      }

      const successAlert = await Swal.fire({
        icon: 'success',
        text: data?.message || `${activeMaster.singularLabel} added successfully.`,
        confirmButtonColor: '#0f7a8f',
      });

      if (successAlert.isConfirmed) {
        setAddForm({ name: '', status: STATUS_CODE.ACTIVE, sequence: '' });
        setFormErrors({});
        setIsAddModalOpen(false);
        await fetchPosts();
      }
    } catch (error) {
      setIsSubmitting(false);
      await Swal.fire({
        icon: 'error',
        text: error?.message || `Unable to add ${activeMaster.singularLabel.toLowerCase()}.`,
        confirmButtonColor: '#c7374e',
      });
    }
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();

    if (!selectedPost?.id) {
      return;
    }

    if (!validatePostForm(editForm)) {
      return;
    }

    setIsSubmitting(true);

    try {
      const coords = await getCurrentLocationOrNull();

      const { ok, data } = await apiRequest(`${activeMaster.endpoints.update}/${selectedPost.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: editForm.name.trim(),
          status: Number(editForm.status),
          sequence: Number(editForm.sequence),
          latitude: coords.latitude,
          longitude: coords.longitude,
        }),
      });

      setIsSubmitting(false);

      if (!ok || data?.response !== true) {
        await Swal.fire({
          icon: 'error',
          text: data?.message || `Unable to update ${activeMaster.singularLabel.toLowerCase()}.`,
          confirmButtonColor: '#c7374e',
        });
        return;
      }

      const successAlert = await Swal.fire({
        icon: 'success',
        text: data?.message || `${activeMaster.singularLabel} updated successfully.`,
        confirmButtonColor: '#0f7a8f',
      });

      if (successAlert.isConfirmed) {
        setIsEditModalOpen(false);
        setSelectedPost(null);
        setFormErrors({});
        await fetchPosts();
      }
    } catch (error) {
      setIsSubmitting(false);
      await Swal.fire({
        icon: 'error',
        text: error?.message || `Unable to update ${activeMaster.singularLabel.toLowerCase()}.`,
        confirmButtonColor: '#c7374e',
      });
    }
  };

  const handleDelete = async (id) => {
    if (!id) {
      return;
    }

    const confirmResult = await Swal.fire({
      icon: 'warning',
      title: `Delete ${activeMaster.singularLabel}?`,
      text: `This will permanently delete the selected ${activeMaster.singularLabel.toLowerCase()}.`,
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#c7374e',
      cancelButtonColor: '#6b7280',
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    setIsSubmitting(true);

    try {
      const { ok, data } = await apiRequest(`${activeMaster.endpoints.delete}/${id}`, {
        method: 'POST',
      });

      setIsSubmitting(false);

      if (!ok || data?.response !== true) {
        await Swal.fire({
          icon: 'error',
          text: data?.message || `Unable to delete ${activeMaster.singularLabel.toLowerCase()}.`,
          confirmButtonColor: '#c7374e',
        });
        return;
      }

      await Swal.fire({
        icon: 'success',
        text: data?.message || `${activeMaster.singularLabel} deleted successfully.`,
        confirmButtonColor: '#0f7a8f',
      });

      await fetchPosts();
    } catch (error) {
      setIsSubmitting(false);
      await Swal.fire({
        icon: 'error',
        text: error?.message || `Unable to delete ${activeMaster.singularLabel.toLowerCase()}.`,
        confirmButtonColor: '#c7374e',
      });
    }
  };

  const handleLogout = async () => {
    try {
      await apiRequest(
        environment.endpoints.logout,
        {
          method: 'POST',
        },
        { skipValidation: true }
      );
    } catch {
      // Ignore logout API failure and continue client-side cleanup.
    } finally {
      localStorage.removeItem('authUser');
      sessionStorage.removeItem('authUser');
      localStorage.setItem('logoutRequested', 'true');
      sessionStorage.setItem('logoutRequested', 'true');
      navigate('/login', { replace: true });
    }
  };

  const switchMaster = (masterKey) => {
    setActiveMasterKey(masterKey);
    setSearchText('');
    setFormErrors({});
    setAddForm({ name: '', status: STATUS_CODE.ACTIVE, sequence: '' });
    setEditForm({ name: '', status: STATUS_CODE.ACTIVE, sequence: '' });
    setSelectedPost(null);
    setIsAddModalOpen(false);
    setIsViewModalOpen(false);
    setIsEditModalOpen(false);
  };

  // Set next sequence when opening add modal
  const openAddModal = () => {
    setFormErrors({});
    setAddForm({ name: '', status: STATUS_CODE.ACTIVE, sequence: nextSequence });
    setIsAddModalOpen(true);
  };

  const sections = useMemo(
    () => createSidebarSections(navigate, 'master'),
    [navigate]
  );

  return (
    <DashboardLayout
      displayName={userName}
      pageTitle={`Masters`}
      dashboard={{
        active: false,
        onClick: () => navigate('/welcome'),
      }}
      sections={sections}
      onLogout={handleLogout}
    >
      <section className="master-shell-card">
          <aside className="master-left-nav">
            <h3>Masters</h3>
            <button
              type="button"
              className={`master-left-item ${activeMasterKey === 'posts' ? 'active' : ''}`}
              onClick={() => switchMaster('posts')}
            >
              Posts
            </button>
            <button
              type="button"
              className={`master-left-item ${activeMasterKey === 'educationType' ? 'active' : ''}`}
              onClick={() => switchMaster('educationType')}
            >
              Education Type
            </button>
            <button
              type="button"
              className={`master-left-item ${activeMasterKey === 'jobType' ? 'active' : ''}`}
              onClick={() => switchMaster('jobType')}
            >
              Job Type
            </button>
            {/* <button
              type="button"
              className={`master-left-item ${activeMasterKey === 'designation' ? 'active' : ''}`}
              onClick={() => switchMaster('designation')}
            >
              Designation
            </button> */}
          </aside>

          <div className="master-content-area">
            <div className="master-toolbar">
              <input
                type="text"
                placeholder={activeMaster.searchPlaceholder}
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
              />
              <button
                type="button"
                className="register-user-btn"
                onClick={openAddModal}
              >
                {`+ New ${activeMaster.singularLabel}`}
              </button>
            </div>

            {isPostsLoading && (
              <div className="users-status" role="status" aria-live="polite">
                <span className="users-loader" aria-hidden="true"></span>
              </div>
            )}

            {!isPostsLoading && postsError && (
              <p className="users-status users-error">{postsError}</p>
            )}

            {!isPostsLoading && !postsError && (
              <div className="users-table-wrap">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>{activeMaster.tableHeader}</th>
                      <th>SEQUENCE</th>
                      <th>STATUS</th>
                      <th>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visiblePosts.length > 0 ? (
                      visiblePosts.map((row, index) => (
                        <tr key={row.id || row.name || `post-${index}`}>
                          <td>{row.name || '-'}</td>
                          <td>{row.sequence || '-'}</td>
                          <td>
                            <span className={`master-status-pill ${getStatusClassName(row.status)}`}>
                              {normalizeStatus(row.status)}
                            </span>
                          </td>
                          <td>
                            <div className="user-action-cell">
                              <button
                                type="button"
                                className="action-icon-btn"
                                onClick={() => openViewModal(row.id)}
                                aria-label={`View ${activeMaster.singularLabel.toLowerCase()}`}
                                title="View"
                              >
                                👁
                              </button>
                              <button
                                type="button"
                                className="action-icon-btn edit-action-btn"
                                onClick={() => openEditModal(row.id)}
                                aria-label={`Edit ${activeMaster.singularLabel.toLowerCase()}`}
                                title="Edit"
                              >
                                <span className="action-edit-icon">✏</span>
                              </button>
                              <button
                                type="button"
                                className="action-icon-btn delete-action-btn"
                                onClick={() => handleDelete(row.id)}
                                aria-label={`Delete ${activeMaster.singularLabel.toLowerCase()}`}
                                title="Delete"
                              >
                                🗑
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="users-empty">{`No ${activeMaster.pluralLabel.toLowerCase()} found.`}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
      </section>

      {isAddModalOpen && (
        <div className="register-modal-overlay" onClick={() => !isSubmitting && setIsAddModalOpen(false)} role="presentation">
          <section className="register-modal animated-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <div className="register-modal-header">
              <h3>{`Add ${activeMaster.singularLabel}`}</h3>
              <button type="button" className="register-modal-close" onClick={() => !isSubmitting && setIsAddModalOpen(false)}>X</button>
            </div>

            <form className="register-user-form" onSubmit={handleAddSubmit} noValidate>
              <div className="register-user-grid">
                <div className="register-field">
                  <label htmlFor="master-name">{activeMaster.inputLabel}</label>
                  <input id="master-name" name="name" value={addForm.name} onChange={handleAddChange} placeholder={activeMaster.inputPlaceholder} disabled={isSubmitting} />
                  {formErrors.name && <p className="register-error">{formErrors.name}</p>}
                </div>

                <div className="register-field">
                  <label htmlFor="master-sequence">Sequence</label>
                  <input 
                    id="master-sequence" 
                    name="sequence" 
                    type="number"
                    value={addForm.sequence} 
                    onChange={handleAddChange} 
                    placeholder="Auto-generated sequence" 
                    disabled={isSubmitting}
                    min="1"
                  />
                  {formErrors.sequence && <p className="register-error">{formErrors.sequence}</p>}
                </div>

                <div className="register-field">
                  <label htmlFor="post-status">Status</label>
                  <select id="post-status" name="status" value={addForm.status} onChange={handleAddChange} disabled={isSubmitting}>
                    <option value={STATUS_CODE.ACTIVE}>Active</option>
                    <option value={STATUS_CODE.INACTIVE}>Inactive</option>
                  </select>
                </div>
              </div>

              <button type="submit" className="register-user-btn" disabled={isSubmitting}>{`Add ${activeMaster.singularLabel}`}</button>
            </form>
          </section>
        </div>
      )}

      {isViewModalOpen && selectedPost && (
        <div className="register-modal-overlay" onClick={() => setIsViewModalOpen(false)} role="presentation">
          <section className="register-modal animated-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <div className="register-modal-header">
              <h3>{`View ${activeMaster.singularLabel}`}</h3>
              <button type="button" className="register-modal-close" onClick={() => setIsViewModalOpen(false)}>X</button>
            </div>

            <div className="view-user-details">
              <div className="view-row"><span>Name</span><strong>{selectedPost.name || '-'}</strong></div>
              <div className="view-row"><span>Sequence</span><strong>{selectedPost.sequence || '-'}</strong></div>
              <div className="view-row"><span>Status</span><strong>{normalizeStatus(selectedPost.status)}</strong></div>
              <div className="view-row"><span>Latitude</span><strong>{selectedPost.latitude || '-'}</strong></div>
              <div className="view-row"><span>Longitude</span><strong>{selectedPost.longitude || '-'}</strong></div>
            </div>
          </section>
        </div>
      )}

      {isEditModalOpen && selectedPost && (
        <div className="register-modal-overlay" onClick={() => !isSubmitting && setIsEditModalOpen(false)} role="presentation">
          <section className="register-modal animated-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <div className="register-modal-header">
              <h3>{`Edit ${activeMaster.singularLabel}`}</h3>
              <button type="button" className="register-modal-close" onClick={() => !isSubmitting && setIsEditModalOpen(false)}>X</button>
            </div>

            <form className="register-user-form" onSubmit={handleEditSubmit} noValidate>
              <div className="register-user-grid">
                <div className="register-field">
                  <label htmlFor="edit-master-name">{activeMaster.inputLabel}</label>
                  <input id="edit-master-name" name="name" value={editForm.name} onChange={handleEditChange} placeholder={activeMaster.inputPlaceholder} disabled={isSubmitting} />
                  {formErrors.name && <p className="register-error">{formErrors.name}</p>}
                </div>

                <div className="register-field">
                  <label htmlFor="edit-master-sequence">Sequence</label>
                  <input 
                    id="edit-master-sequence" 
                    name="sequence" 
                    type="number"
                    value={editForm.sequence} 
                    onChange={handleEditChange} 
                    placeholder="Enter sequence" 
                    disabled={isSubmitting}
                    min="1"
                  />
                  {formErrors.sequence && <p className="register-error">{formErrors.sequence}</p>}
                </div>

                <div className="register-field">
                  <label htmlFor="edit-post-status">Status</label>
                  <select id="edit-post-status" name="status" value={editForm.status} onChange={handleEditChange} disabled={isSubmitting}>
                    <option value={STATUS_CODE.ACTIVE}>Active</option>
                    <option value={STATUS_CODE.INACTIVE}>Inactive</option>
                  </select>
                </div>
              </div>

              <button type="submit" className="register-user-btn" disabled={isSubmitting}>{`Update ${activeMaster.singularLabel}`}</button>
            </form>
          </section>
        </div>
      )}

      {isSubmitting && (
        <div className="api-loader-overlay">
          {renderLogoLoader()}
        </div>
      )}
    </DashboardLayout>
  );
};

export default MasterPage;